import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  Search,
  Delete,
  AdminPanelSettings,
  PersonOff,
  PersonOutline,
  PersonAdd,
  Visibility,
  VisibilityOff,
  Edit,
} from "@mui/icons-material";
import { usersAdminAPI, AdminUser } from "../../services/adminApi";

const PLAN_COLORS: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
  basic: "primary",
  premium: "success",
  pro: "warning",
  enterprise: "error",
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Add user dialog
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    phone: "",
  });

  // Edit user dialog
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", role: "user" });

  const openEditDialog = (u: AdminUser) => {
    setEditTarget(u);
    setEditForm({ name: u.name, email: u.email, phone: u.phone || "", role: u.role });
    setEditError("");
  };

  const handleEditUser = async () => {
    if (!editTarget) return;
    setEditError("");
    if (!editForm.name.trim() || !editForm.email.trim()) {
      setEditError("Name and email are required");
      return;
    }
    setEditLoading(true);
    try {
      await usersAdminAPI.update(editTarget._id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        role: editForm.role,
      });
      setActionMsg(`${editForm.name.trim()} updated successfully`);
      setEditTarget(null);
      fetchUsers();
    } catch (e: any) {
      setEditError(e.response?.data?.message || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  // Action feedback
  const [actionMsg, setActionMsg] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await usersAdminAPI.list({
        page,
        limit: 15,
        search: search || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
      });
      setUsers(res.data.data);
      setTotalPages(res.data.pagination.pages);
      setTotalUsers(res.data.pagination.total);
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchUsers, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchUsers, search]);

  const handleToggleActive = async (user: AdminUser) => {
    try {
      await usersAdminAPI.update(user._id, { isActive: !user.isActive });
      setActionMsg(`${user.name} ${user.isActive ? "deactivated" : "activated"}`);
      fetchUsers();
    } catch {
      setError("Failed to update user");
    }
  };

  const handleToggleRole = async (user: AdminUser) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    try {
      await usersAdminAPI.update(user._id, { role: newRole });
      setActionMsg(`${user.name} is now ${newRole}`);
      fetchUsers();
    } catch {
      setError("Failed to update role");
    }
  };


  const handleAddUser = async () => {
    setAddUserError("");
    if (!addUserForm.name.trim() || !addUserForm.email.trim() || !addUserForm.password) {
      setAddUserError("Name, email and password are required");
      return;
    }
    if (addUserForm.password.length < 6) {
      setAddUserError("Password must be at least 6 characters");
      return;
    }
    setAddUserLoading(true);
    try {
      await usersAdminAPI.create({
        name: addUserForm.name.trim(),
        email: addUserForm.email.trim(),
        password: addUserForm.password,
        role: addUserForm.role,
        phone: addUserForm.phone.trim() || undefined,
      });
      setActionMsg(`User "${addUserForm.name.trim()}" created successfully`);
      setAddUserOpen(false);
      setAddUserForm({ name: "", email: "", password: "", role: "user", phone: "" });
      setShowPassword(false);
      fetchUsers();
    } catch (e: any) {
      setAddUserError(e.response?.data?.message || "Failed to create user");
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await usersAdminAPI.delete(deleteTarget._id);
      setActionMsg(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0A1628", mb: 0.5 }}>
            User Management
          </Typography>
          <Typography variant="body2" sx={{ color: "#78909C" }}>
            {totalUsers} total users
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => { setAddUserOpen(true); setAddUserError(""); }}
          sx={{
            backgroundColor: "#1565C0",
            "&:hover": { backgroundColor: "#0D47A1" },
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Add User
        </Button>
      </Box>

      {actionMsg && (
        <Alert severity="success" onClose={() => setActionMsg("")} sx={{ mb: 3 }}>
          {actionMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search name or emailâ€¦"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F0F7FF" }}>
              <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Subscription</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#0A1628" }}>Joined</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#0A1628" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#78909C" }}>
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow
                  key={u._id}
                  sx={{
                    opacity: u.isActive ? 1 : 0.5,
                    "&:hover": { backgroundColor: "#FAF7F4" },
                  }}
                >
                  {/* User */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar src={u.avatar} sx={{ width: 34, height: 34, fontSize: "0.85rem" }}>
                        {u.name[0]}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#0A1628" }}>
                        {u.name}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Email */}
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "#78909C", fontSize: "0.82rem" }}>
                      {u.email}
                    </Typography>
                  </TableCell>

                  {/* Role */}
                  <TableCell>
                    <Chip
                      label={u.role}
                      size="small"
                      color={u.role === "admin" ? "warning" : "default"}
                      sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={u.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={u.isActive ? "success" : "error"}
                      sx={{ fontSize: "0.75rem" }}
                    />
                  </TableCell>

                  {/* Subscription */}
                  <TableCell>
                    {u.subscription ? (
                      <Chip
                        label={`${u.subscription.plan} / ${u.subscription.billingCycle}`}
                        size="small"
                        color={PLAN_COLORS[u.subscription.plan] || "default"}
                        sx={{ fontSize: "0.72rem" }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: "#BBB" }}>None</Typography>
                    )}
                  </TableCell>


                  {/* Joined */}
                  <TableCell>
                    <Typography variant="caption" sx={{ color: "#78909C" }}>
                      {new Date(u.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {/* Edit user */}
                      <Tooltip title="Edit user">
                        <IconButton size="small" onClick={() => openEditDialog(u)} sx={{ color: "#43A047" }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Toggle admin */}
                      <Tooltip title={u.role === "admin" ? "Remove admin" : "Make admin"}>
                        <IconButton size="small" onClick={() => handleToggleRole(u)} sx={{ color: "#1565C0" }}>
                          <AdminPanelSettings fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Toggle active */}
                      <Tooltip title={u.isActive ? "Deactivate" : "Activate"}>
                        <IconButton size="small" onClick={() => handleToggleActive(u)} sx={{ color: "#78909C" }}>
                          {u.isActive ? <PersonOff fontSize="small" /> : <PersonOutline fontSize="small" />}
                        </IconButton>
                      </Tooltip>


                      {/* Delete */}
                      <Tooltip title="Delete user">
                        <IconButton size="small" onClick={() => setDeleteTarget(u)} sx={{ color: "#E57373" }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      )}

      {/* Add User dialog */}
      <Dialog
        open={addUserOpen}
        onClose={() => { setAddUserOpen(false); setAddUserError(""); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#0A1628" }}>Add New User</DialogTitle>
        <DialogContent>
          {addUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>{addUserError}</Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Full Name"
              required
              value={addUserForm.name}
              onChange={(e) => setAddUserForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              required
              value={addUserForm.email}
              onChange={(e) => setAddUserForm((f) => ({ ...f, email: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              required
              value={addUserForm.password}
              onChange={(e) => setAddUserForm((f) => ({ ...f, password: e.target.value }))}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={addUserForm.role}
                onChange={(e) => setAddUserForm((f) => ({ ...f, role: e.target.value }))}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Phone (optional)"
              value={addUserForm.phone}
              onChange={(e) => setAddUserForm((f) => ({ ...f, phone: e.target.value }))}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddUserOpen(false); setAddUserError(""); }}>Cancel</Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={addUserLoading}
            sx={{ backgroundColor: "#1565C0", "&:hover": { backgroundColor: "#0D47A1" }, textTransform: "none" }}
          >
            {addUserLoading ? <CircularProgress size={18} color="inherit" /> : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#0A1628" }}>Edit User</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Full Name"
              required
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              required
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Phone (optional)"
              value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={editForm.role}
                onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button
            onClick={handleEditUser}
            variant="contained"
            disabled={editLoading}
            sx={{ backgroundColor: "#43A047", "&:hover": { backgroundColor: "#2E7D32" }, textTransform: "none" }}
          >
            {editLoading ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;


